import { useMemo } from 'react'

import { matchFilterArrayIncludes, matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'


import type { EntityOptions } from '@redux/modules/types'

import type { ArtworkModel } from '@domain-group/realhub-backend-api-types'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['artworks']

type ArtworkFilters = {
  campaign_id?: number,
  order_item_statuses?: string[],
  template_page_id?: number,
}

type UseArtworksOptions = {
  filters?: ArtworkFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useArtworks(options: UseArtworksOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_id: filterCampaignId,
    order_item_statuses: filterOrderItemStatuses,
    template_page_id: filterTemplatePageId,
  } = filters

  const {
    updatedEntities: {
      artworks: artworksUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { artworks, orderItems, statuses } = useSelector(reduxState => reduxState.entities)


  console.log(orderItems);

  const filteredArtworks = useMemo(() => {
    const filtered = Object.values(artworks).filter((artwork: ArtworkModel) => {
      const {
        campaign_id, template_page_id, order_item_id,
      } = artwork
      const orderItemStatusId = order_item_id ? orderItems[order_item_id]?.status_id : undefined

      const orderItemStatus = orderItemStatusId ? statuses[orderItemStatusId]?.key : undefined

      const matchCampaignId = matchFilterNumber(campaign_id, filterCampaignId)
      const matchTemplatePageId = matchFilterNumber(template_page_id, filterTemplatePageId)
      const matchOrderItemStatuses = filterOrderItemStatuses?.length
        // @ts-expect-error
        ? matchFilterArrayIncludes(filterOrderItemStatuses, orderItemStatus)
        : true

      return matchCampaignId && matchTemplatePageId && matchOrderItemStatuses
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [artworksUpdatedAt, JSON.stringify(filters)])

  const filteredArtworksCount = filteredArtworks.length
  const hasFilteredArtworks = !!filteredArtworksCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredArtworksCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingArtworks } = useReduxAction(
    'artworks',
    'loadArtworks',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredArtworks,
    filteredArtworksCount,
    hasFilteredArtworks,
    loading: loadingArtworks,
  }
}

export default useArtworks

